import _ from 'underscore';
import TemplateHtml from '../../../html/engine/arcade/header.html';
import MenuItemHtml from '../../../html/engine/arcade/menuItem.html';
import BaseView from '../../baseView';
import InputManager from '../../manager/inputManager';

/**
 * @typedef {import('../../baseView').BaseViewOptions} BaseViewOptions
 * @typedef {import('./arcade.types').PageOptions} PageOptions
 *
 * @typedef {object} HeaderPartial
 * @property {string} [image]
 * @property {PageOptions[]} [pages]
 *
 * @typedef {BaseViewOptions & HeaderPartial} HeaderOptions
 */

/**
 * @extends {BaseView<HeaderOptions>}
 */
export default class Header extends BaseView {
	get CN() {
		return Header.name;
	}

	get id() {
		return 'header';
	}

	get className() {
		return 'header empty';
	}

	get template() {
		return _.template(TemplateHtml);
	}

	get menuItemTemplate() {
		return _.template(MenuItemHtml);
	}

	get pageClassNames() {
		return '.home';
	}

	/**
	 * @param {HeaderOptions} options
	 */
	initialize(options) {
		this.logger.log(`[${this.initialize.name}]`, options);

		/** @type {PageOptions[]} */
		this.pages = [];

		super.initialize(options);

		InputManager.add('menu', {
			selector: '#menu .menu-item:not(.disabled), #header a.brand',
			defaultElement: '#header .menu-item.home',
		});

		/** @type {Record<string, PageOptions>} */
		this.pageMap = this.pages.reduce((map, page) => {
			if (page && page.id) {
				map[page.id] = page;
			}
			return map;
		}, {});

		this.render();
	}

	/**
	 * @param {PageOptions} newPageData
	 * @returns
	 */
	updatePage(newPageData) {
		this.logger.log(`[${this.updatePage.name}]`, { newPageData });

		if (!newPageData) {
			this.logger.error('Invalid page data', { newPageData });
			return;
		}

		const {
			visiblePages = [],
			availablePages,
			headerImage = this.options?.headerImage || '',
		} = newPageData || this.pages?.[0] || {};
		const availablePagesSelector = (availablePages || visiblePages)
			.map((page) => `.${page}`)
			.join(', ');

		/** @type {DocumentFragment[]} */
		const menuItems = visiblePages
			.map((pageId) => this.pageMap?.[`${pageId}`])
			.filter((page) => page && page.id)
			.map(this.createMenuItem.bind(this));

		// Hide the hamburger menu if in use
		this.$el.find('input[type=checkbox]').prop('checked', false);

		this.dismissView()
			.then(() => {
				this.$('.brand img').attr('src', headerImage);

				const $menu = this.$el.find('#menu').empty();
				const menu = $menu[0];

				if (!menuItems.length) {
					this.$el.addClass('empty');
					return Promise.resolve();
				}
				this.$el.removeClass('empty');

				// Append each menu item fragment individually
				menuItems.forEach((fragment) => menu?.appendChild(fragment));

				this.$el.find(availablePagesSelector).addClass('available');
				this.attachDecorators();

				return this.displayView();
			})
			.catch((error) => this.logger.error('[Error updating menu]', { error }));
	}

	/**
	 * @param {PageOptions} page
	 */
	createMenuItem(page) {
		this.logger.log(`[${this.createMenuItem.name}]`, { page });

		// Create a document fragment to hold the template result
		const fragment = document.createDocumentFragment();
		const tempContainer = document.createElement('div');

		// Apply template to trusted page data
		tempContainer.innerHTML = this.menuItemTemplate(page);

		// Move nodes to fragment
		while (tempContainer.firstChild) {
			fragment.appendChild(tempContainer.firstChild);
		}

		return fragment;
	}
}
